import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  MenuItem,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "commons/store/hooks";
import ColoredBox from "components/ColoredBox";
import { Table } from "components/Table";
import { fetchDrugVisits, provideDrug } from "features/ecrf/redux/ecrfSlice";
import { useSnackbar } from "notistack";
import DateTime from "core/helpers/date/DateTime";
import { selectUserProfile } from "core/redux/userSlice";
import { roles } from 'commons/auth/roles';
import { DATE_FORMAT, DATETIME_FORMAT, MODULE_DM } from "commons/constants/env";
import { useTranslation } from "react-i18next";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { formatISO, isValid } from "date-fns";
import { toBool } from "lib";
import { RowDetails } from "./components";
import { exportDrugAllocation, selectEcrfResourceLoading } from "features/ecrf/redux/ecrfDataManagementSlice";
import { fetchEcrfDrugAllocation } from "../../redux/dashboardSlice";
import { MIME_TYPE } from "utils/dataAccess";

const DashboardProvide = () => {
    const dispatch = useAppDispatch();
    const [page, setPage] = useState(1);
    const initialVisitsArray: string[] = new Array(100).fill('');
    const initialDrugArray: string[] = new Array(100).fill('');
    const initialExpiresArray: string[] = new Array(100).fill('');
    const initialReleaseArray: string[] = new Array(100).fill('');
    const [visit, setVisit] = useState(initialVisitsArray);
    const [drugNumber, setDrugNumber] = useState(initialDrugArray);
    const [drugExpire, setDrugExpire] = useState(initialExpiresArray);
    const [drugRelease, setDrugRelease] = useState(initialReleaseArray);
    const [drugEmerg, setDrugEmerg] = useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const [confirmationModal, setConfirmationModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState() as any;
    const [selectedIndex, setSelectedIndex] = useState() as any;
    const [showManualDoseValue, setShowManualDoseValue] = useState(false);
    const [manualValue, setManualValue] = useState<string>('');
    const [isDataLoading, setIsDataLoading] = useState(false)
    const [isProcessing, setIsProcessing] = useState(false);
    const userProfile = useAppSelector(selectUserProfile);
    const isEcrfResourceLoading = useAppSelector(selectEcrfResourceLoading);
    const { t } = useTranslation();

    const dmSupport: boolean = toBool(MODULE_DM);

    const [collection, setCollection] = useState<any>({
        itemsPerPage: 0,
        totalItems: 0,
        member: []
    })

    /**
     * This function is used to fetch visits
     */
    const [visits, setVisits] = useState<any[]>([]);
    const fetchVisitList = async () => {
        const visits = await dispatch(fetchDrugVisits()).unwrap();
        setVisits(visits);
    }

    const fetchData = async () => {
        setIsDataLoading(true);

        const config = {
          headers: {
            Accept: MIME_TYPE,
          },
          params: {
            pagination: true,
            page,
            itemsPerPage: 30,
          },
        };

        try {
            const response = await dispatch(fetchEcrfDrugAllocation(config)).unwrap();
            setCollection(response);
        } catch (error: any) {
            enqueueSnackbar(error?.message || error?.detail, { variant: 'error' });
        }

        setIsDataLoading(false);
    }

    const displayExportButton: boolean = userProfile.roles.some(
        (role: string) => roles.admin.includes(role)
            || roles.monitor.includes(role)
            || roles.pharmacist.includes(role)
    );

    const isReadOnly: boolean = userProfile.roles.some(
        (role: string) => roles.monitor.includes(role)
    );

    const handleClose = () => {
        setConfirmationModal(false);
        setShowManualDoseValue(false);
        setManualValue("");
        setSelectedRow();
        setSelectedIndex();
    };

    const handleDispense = (index: any, row: any) => {
        setConfirmationModal(true);
        setSelectedIndex(index);
        setSelectedRow(row);
    };

    const handleNewValue = (e: any) => {
        if (parseFloat(e.target.value) < 0) setManualValue('0');
        else if (parseFloat(e.target.value) > 50) setManualValue('50');
        else {
            setManualValue(e.target.value);
        }
    };

    const drugProvider = async (row: any, index: number) => {
        if (!row) return;

        setIsProcessing(true);

        const type = (row?.drugAllocation && row?.drugAllocation?.[0]?.type)
            ? row?.drugAllocation?.[0]?.type
            : 'd';

        let drugs = [];
        switch (type) {
            case 'dm':
                drugs = [
                    {
                        visit: `/api/visits/${visit[index]}`,
                        number: drugNumber[index],
                        expiredAt: drugExpire[index] ?? null,
                        releasedAt: drugRelease[index] || new Date(),
                        status: (drugEmerg[index] === true) ? 3 : 0,
                        // drugManagement: '/api/drug_managements/' + id,
                    }
                ];
                break;
            default:
                drugs = showManualDoseValue ?
                    [
                        {
                            number: drugNumber[index],
                            drug: row?.drugGroup?.drugs[0]?._links?.self?.href,
                            status: 1,
                            dose: parseFloat(manualValue),
                        }
                    ]
                    :
                    [
                        {
                            number: drugNumber[index],
                            drug: row?.drugGroup?.drugs[0]?._links?.self?.href,
                        }
                    ];
                break;
        }

        try {
            await dispatch(provideDrug({ id: row.id, drugs: drugs }));

            setVisit(initialVisitsArray);
            setDrugNumber(initialDrugArray);
            setDrugExpire(initialExpiresArray);
            setDrugRelease(initialReleaseArray);

            enqueueSnackbar(`${t('drug-has-been_dispensed-for-patient')} ${row?.randomId}`, {
                variant: 'success',
            });

            handleClose();

            await fetchData();
        } catch (error: any) {
            handleClose();
            enqueueSnackbar(error.detail, { variant: 'error' });
        }

        setIsProcessing(false);
    };

    const ecrfStatusClosed = (status: number) => {
        switch (status) {
            case 1:
            case 2:
            case 6:
            case 5:
                return true;
            default:
                return false;
        }
    }

    useEffect(() => {
        fetchVisitList().then()
        fetchData().then();
    }, [dispatch, page]);

    return (
        <ColoredBox pt={0} px={5} maxWidth="100%" ml="auto" mr="auto">
            <Box display={'flex'} gap={'20px'} justifyContent={'end'}>
                <Box mt={'20px'}>
                    {displayExportButton &&
                        <Button
                            size={'small'}
                            color={'primary'}
                            variant={'outlined'}
                            onClick={() => dispatch(exportDrugAllocation(dmSupport ? 'dm' : 'd'))}
                            disabled={isEcrfResourceLoading}
                            startIcon={
                              isEcrfResourceLoading ? <CircularProgress size={12} /> : undefined
                            }
                        >
                            {t('export')}
                        </Button>
                    }
                </Box>
            </Box>

            <Table
                pagination={{
                    pageSize: collection?.itemsPerPage || 50,
                    current: page,
                    total: collection?.totalItems,
                }}
                onChangePage={(page) => setPage(page)}
                data={collection?.['member'] ?? []}
                isLoading={isDataLoading}
                isOpenable={true}
                tableName={t('drug-allocations')}
                columns={[
                    {
                        title: <Box>{t('screening-number')}</Box>,
                        render: (row: any) => (
                            <Box
                                p={1}
                                style={{
                                  opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%',
                                  backgroundColor: row?.drugs ? "" : "#6BDFE1",
                                  borderRadius: row?.drugs ? "" : "5px",
                                  fontWeight: row?.drugs ? 400 : 700,
                                  textAlign: "center",
                                  minWidth: 60
                                }}
                            >
                                {row?.screeningNumber}
                            </Box>
                        ),
                        key: 'screeningNumber',
                    },
                    {
                        title: <Box>{t('randomization-number')}</Box>,
                        render: (row: any) => (
                            <Box p={1} style={{ minWidth: 60}}>
                                {row?.randomId}
                            </Box>
                        ),
                        key: 'randomId',
                    },
                    {
                        title: <Box ml="20px">{t('randomization-date')}</Box>,
                        render: (row: any) => (
                            <Box style={{opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%'}} ml="20px">
                              {DateTime.toIso(row?.randomAt, DATETIME_FORMAT)}
                            </Box>
                        ),
                        key: 'randomDate',
                    },
                    {
                        title: <Box ml="20px" fontWeight="bold">{t('preparation-for-release')}</Box>,
                        render: (row: any) => <Box style={{ opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%' }} ml="20px">
                            {row?.drugAllocation && row?.drugAllocation?.[0]?.drugName}
                        </Box>,
                        key: 'drugName',
                    },
                    {
                        title: <Box ml="20px">{t('preparation-amount')}</Box>,
                        render: (row: any) => (
                            <Box
                                style={{ opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%' }} ml="20px">
                                {row?.drugAllocation && row?.drugAllocation?.[0]?.dose}
                                {row?.drugAllocation && row?.drugAllocation?.[0]?.unit}
                            </Box>
                        ),
                        key: 'drugValue',
                    },
                    {
                        title: <Box ml="20px">{t('body-weight')}</Box>,
                        render: (row: any) => (
                            <Box style={{opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%'}} ml="20px">
                              {row?.currentBodyWeight ? `${row?.currentBodyWeight}g` : ""}
                            </Box>
                        ),
                        key: 'bodyWeight',
                        hide: dmSupport
                    },
                    {
                        title: <Box ml="20px">{t('visit')}</Box>,
                        render: (row: any, index) => {
                            let fVisits = visits;
                            if (drugEmerg[index] === undefined || !drugEmerg[index]) {
                                const uVisits = row?.drugs?.map((drug: any) => drug?.visit?.id);
                                fVisits = (uVisits?.length > 0) ? visits.filter((v) => !uVisits.includes(v.id)) : visits;
                            }

                            return (
                                <Box style={{ opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%' }} pt={1} pb={1}>
                                    <TextField
                                        select
                                        fullWidth
                                        variant={'outlined'}
                                        size={'small'}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            let visits: any = visit;
                                            visits[index] = e.target.value;
                                            setVisit(visits.slice());
                                        }}
                                        defaultValue={''}
                                    >
                                        {/** Display list of all numbers */}
                                        {fVisits.map((value: any) => (
                                            <MenuItem key={value?.id} value={value?.id}>
                                                {value?.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            )
                        },
                        key: 'visit',
                        hide: !dmSupport
                    },
                    {
                        title: <Box ml="20px">{t('emerg-release')}</Box>,
                        render: (row: any, index) => {
                            return (
                                <Box style={{ opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%' }} pt={1} pb={1}>
                                    <Checkbox
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            let emerg: any = [];
                                            emerg[index] = e.target.checked;
                                            setDrugEmerg(emerg.slice());
                                        }}
                                    />
                                </Box>
                            )
                        },
                        key: 'emergRelease',
                        hide: !dmSupport
                    },
                    {
                        title: <Box ml="20px">{t('series-and-number')}</Box>,
                        render: (row: any, index) => (
                            <Box style={{opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%'}} pt={1} pb={1}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    id="outlined-basic"
                                    variant="outlined"
                                    disabled={ecrfStatusClosed(row?.status)}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        let drugs: any = drugNumber;
                                        drugs[index] = e.target.value;
                                        setDrugNumber(drugs.slice());
                                    }}
                                    sx={{ minWidth: 150 }}
                                />
                            </Box>
                        ),
                        key: 'number',
                    },
                    {
                        title: <Box ml="20px">{t('expiration-date')}</Box>,
                        render: (row: any, index) => (
                            <Box style={{opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%'}} pt={1} pb={1}>
                                <DatePicker
                                    disabled={ecrfStatusClosed(row?.status)}
                                    format={DATE_FORMAT}
                                    onChange={(newValue: any) => {
                                        let expires: any = drugExpire;
                                        expires[index] = isValid(newValue) && formatISO(newValue);
                                        setDrugExpire(expires.slice());
                                    }}
                                    disablePast={true}
                                    slotProps={{
                                        actionBar: {
                                            actions: ['clear']
                                        },
                                        textField: {
                                            size: 'small',
                                            variant: 'outlined',
                                            fullWidth: true
                                        }
                                    }}
                                    sx={{ minWidth: 150 }}
                                />
                            </Box>
                        ),
                        key: 'expiredAt',
                        hide: !dmSupport
                    },
                    {
                        title: <Box ml="20px">{t('release-date')}</Box>,
                        render: (row: any, index) => (
                            <Box style={{ opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%' }} pt={1} pb={1}>
                                <DateTimePicker
                                    disabled={ecrfStatusClosed(row?.status)}
                                    format={DATETIME_FORMAT}
                                    onChange={(newValue: any) => {
                                        let release: any = drugRelease;
                                        release[index] = isValid(newValue) && formatISO(newValue);
                                        setDrugRelease(release.slice());
                                    }}
                                    disableFuture={true}
                                    slotProps={{
                                        actionBar: {
                                            actions: ['clear']
                                        },
                                        textField: {
                                            size: 'small',
                                            variant: 'outlined',
                                            fullWidth: true
                                        }
                                    }}
                                    sx={{ minWidth: 180 }}
                                />
                            </Box>
                        ),
                        key: 'releasedAt',
                        hide: !dmSupport
                    },
                    {
                        title: <Box ml="20px">{t('release')}</Box>,
                        render: (row: any) => (
                            <Box style={{ opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%' }}>
                                {row?.drugs &&
                                  <Typography style={{ fontWeight: "bold" }}>
                                    {DateTime.toIso(
                                      row?.drugs?.at(0)?.releasedAt ||
                                      row?.drugs?.at(0)?.createdAt,
                                      DATE_FORMAT
                                    )}
                                  </Typography>
                                }
                            </Box>
                        ),
                        key: 'dateOfProvide',
                    },
                    {
                        title: <Box ml="20px">{t('actions')}</Box>,
                        noWrap: true,
                        render: (row: any, index) => (
                            <Box style={{ opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%' }} ml="20px">
                                <Button
                                    type="button"
                                    onClick={() => handleDispense(index, row)}
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    style={{ width: "100%" }}
                                    disabled={(dmSupport
                                            ? (visit[index].length < 3 || drugNumber[index].length < 3 || !drugExpire[index])
                                            : drugNumber[index].length < 3
                                        ) || isReadOnly}
                                >
                                    {row?.drugs ? t('dispense-again') : t('dispense-dose')}
                                </Button>
                            </Box>
                        ),
                        key: 'actions',
                    },
                ]}
                maxHeight={600}
                rowDetails={(row, index) => (
                  <RowDetails row={row} />
                )}
            />

            {/** Modal with confirmation action, uses params from state */}
            <Dialog open={confirmationModal} onClose={handleClose} fullWidth>
                <DialogTitle style={{ opacity: showManualDoseValue ? ".3" : "1" }}>
                    {t('do-you confirm-that-the-calculated-dose-is-correct') + " "}
                    {!dmSupport && (selectedRow?.drugAllocation?.[0]?.saline === 3 ? "(3ml)" : "(0,15mg/kg masy ciała)")}?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {showManualDoseValue && (
                            <Box
                                alignItems="center"
                                display="flex"
                                color="black"
                                fontWeight="bold"
                                fontSize="20px"
                            >
                                <Box mr={2} fontSize="18px">
                                    {t('give-the-correct-dose')}:
                                </Box>
                                <TextField
                                    onChange={handleNewValue}
                                    size="small"
                                    id="outlined-basic"
                                    variant="outlined"
                                    value={manualValue}
                                    type="number"
                                />
                                <Box ml={2}>{selectedRow?.drugAllocation?.[0]?.unit}</Box>
                            </Box>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    {showManualDoseValue && (
                        <Button onClick={handleClose} color="primary">
                            {t('cancel')}
                        </Button>
                    )}

                    {!showManualDoseValue && (
                        <Button
                            onClick={() => setShowManualDoseValue(true)}
                            color="secondary"
                            variant="contained"
                        >
                            {t('no')}
                        </Button>
                    )}

                    <Button
                        onClick={() => drugProvider(selectedRow, selectedIndex)}
                        color="primary"
                        autoFocus
                        variant="contained"
                        disabled={isProcessing}
                    >
                        {isProcessing ? (
                            <CircularProgress size={22}/>
                        ) : (
                            <Box>{showManualDoseValue ? t('spend') : t('yes')}</Box>
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </ColoredBox>
    );
};

export default DashboardProvide;
